const HYDRATION_CACHE = {}
const HYDRATION_SMALL_CACHE = {}
const SKELETON_ATTRUBUTES = [
  'data-product-href',
  'data-product-image-primary',
  'data-product-image-secondary',
  'data-product-title',
  'data-product-description',
  'data-product-price',
]

import { css, getProductData, styles } from '@hh/global.js'
const { visuallyHidden } = styles

class MiniProductCard extends HHBaseElement {
  constructor() {
    super()

    this._hydrated = !this.shouldHydrate
    this._skeletonTemplate = document.getElementById('skeleton-product-card')

    this.sizeButtons = [
      ...this.querySelectorAll('.size-variants-container button'),
    ]

    this.selectedVariantInput = this.querySelector('.js-selected-variant')

    this.slideoutToggle = this.querySelector('.product-card__edit-variant')

    this.productForm = [...this.querySelectorAll('form')]

    this.swatchSliderCreated = false

    this.imageParent = this.querySelector('.product-card__image-container')

    this.imageOne = this.querySelector(
      '.product-card__image-one adaptive-image'
    )

    this.mobileImage = this.querySelector(
      '.product-card__mobile-image adaptive-image'
    )

    this.slideout = document.querySelector('.slideout-swatch-selector')
    this.slideoutSwatchContainer = this.slideout.querySelector(
      '.swatches-container'
    )
    this.slideoutTitle = document.querySelector(
      '.slideout-swatch-selector .product__title'
    )
    this.slideoutCategories = document.querySelector(
      '.slideout-swatch-selector .swatch-category-slider'
    )
    this.slideoutSwatches = document.querySelector(
      '.slideout-swatch-selector .swatches-container ul'
    )
    this.slideoutHeader = document
      .querySelector('.slide-panel--slideout[slideout-id="swatch-selector"]')
      .shadowRoot.querySelector('.slide-panel__title-container')
    this.slideoutPanel = document
      .querySelector('.slide-panel--slideout[slideout-id="swatch-selector"]')
      .shadowRoot.querySelector('.slide-panel__panel')
    this.mql = window.matchMedia('(min-width: 768px)')

    this._updateCardVariantState = this._updateCardVariantState.bind(this)
    this._updateSlideoutVariant = this._updateSlideoutVariant.bind(this)
    this._clearActiveButton = this._clearActiveButton.bind(this)
    this._updateSlideoutContent = this._updateSlideoutContent.bind(this)
    this._formatCurrency = this._formatCurrency.bind(this)
    this._handleize = this._handleize.bind(this)
    this._createSwatchGroupSlider = this._createSwatchGroupSlider.bind(this)
    this._handleSlideoutFilter = this._handleSlideoutFilter.bind(this)
    this._handleSlideoutFilterKeydown =
      this._handleSlideoutFilterKeydown.bind(this)
    this._createSwatchFilterButton = this._createSwatchFilterButton.bind(this)
    this._swapSlideoutFeaturedVariant =
      this._swapSlideoutFeaturedVariant.bind(this)
  }

  connectedCallback() {
    this._attachListeners()
    if (this.shouldHydrate) {
      return this._hydrate()
    }
  }

  async _hydrate() {
    const cacheVar =
      this.cardSize === 'small' ? HYDRATION_SMALL_CACHE : HYDRATION_CACHE
    const viewParam = 'mini-product-card'

    if (cacheVar[this.productHandle]) {
      this.innerHTML = cacheVar[this.productHandle]

      this._handleHydration()
    } else {
      if (this.cardSize !== 'small') this._renderSkeleton()
      const baseUrl = window.routes.root_url.endsWith('/')
        ? window.routes.root_url
        : `${window.routes.root_url}/`
      const request = await fetch(
        `${baseUrl}products/${this.productHandle}?view=${viewParam}`
      )
      const container = document.createElement('div')
      const returnedHtml = await request.text()
      container.innerHTML = returnedHtml

      cacheVar[this.productHandle] =
        container.querySelector(viewParam).innerHTML
      this.innerHTML = cacheVar[this.productHandle]
      this._hydrated = true
      this._handleHydration()
    }

    if (this.dataset.atcLabel) {
      const atcLabel = this.querySelector('button[type="submit"] span')
      if (atcLabel) atcLabel.innerHTML = this.dataset.atcLabel
    }
    if (
      this.dataset.editVariantLabel &&
      this.querySelector('button[data-target="swatch-selector"] span')
    ) {
      this.querySelector(
        'button[data-target="swatch-selector"] span'
      ).innerHTML = this.dataset.editVariantLabel
    }
  }
  _renderSkeleton() {
    if (!this._skeletonTemplate) return null
    const contents = this._skeletonTemplate.cloneNode(true)
    this.innerHTML = contents.innerHTML
    SKELETON_ATTRUBUTES.forEach((attr) => {
      const data = this.getAttribute(attr)
      if (!!data) {
        const targets = Array.from(this.querySelectorAll(`[${attr}]`))
        targets.forEach((target) => {
          if (target.tagName === 'ADAPTIVE-IMAGE') {
            target.setAttribute('data-image', data)
          } else if (target.tagName === 'A') {
            target.setAttribute('href', data)
          } else {
            target.innerHTML = data
          }
        })
      }
    })
  }

  _handleHydration() {
    this._product = undefined
    this._selectItems()
    this._removeListeners()
    this._attachListeners()
    this.classList.add('hydrated')
    this._setURLPostfix()
  }

  _setURLPostfix() {
    const urlPostfix = this.getAttribute('url-postfix')

    if (!!urlPostfix) {
      try {
        const links = this.querySelectorAll('.js-product-card-link')
        for (let i = 0; i < links.length; i++) {
          const link = links[i]
          if (link && !!link.href) {
            const href = new URL(link.href)
            href.searchParams.append(...urlPostfix.split('='))
            link.href = href.toString()
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  _selectItems() {
    this.addToCartToggle = this.querySelector('.js-quick-shop-atc')
    this.quickShopToggle = this.querySelector('.js-toggle-quick-shop')
  }

  get product() {
    return (this._product ??= getProductData(this.productId, this))
  }

  get productHandle() {
    return this.getAttribute('product-handle')
  }

  get shouldHydrate() {
    return this.hasAttribute('hydrate')
  }

  get colorSwatches() {
    return [...this.querySelectorAll('.swatch-element')]
  }

  get variantType() {
    return this.getAttribute('variantType')
  }

  get atcButton() {
    return this.querySelector('.atc-container button')
  }

  get swatchGroupData() {
    return JSON.parse(
      window.atob(this.querySelector(`.swatch-groups-data`)?.innerText)
    )
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get swatchImageUrl() {
    return (
      this.querySelector('.product-card__inner')?.getAttribute(
        'data-swatch-image-url'
      ) || 'https://buxomcosmetics.com/cdn/shop/files/Lips.png?v=1685021582'
    )
  }

  static get observedAttributes() {
    return ['variantId']
  }

  _attachListeners() {
    if (this.variantType === 'color') {
      this?.colorSwatches?.forEach((swatch) =>
        swatch.addEventListener('click', this._updateCardVariantState)
      )
    } else if (this.variantType === 'size') {
      this?.sizeButtons?.forEach((button) =>
        button.addEventListener('click', this._updateCardVariantState)
      )
    }

    this.slideoutToggle = this.querySelector('.product-card__edit-variant')

    if (this.slideoutToggle) {
      this.slideoutToggle.addEventListener('click', () => {
        this._updateSlideoutContent()
        this._handleNostoAttribution()
      })
    }
    // this.productForm.addEventListener('submit', this.addToCart.bind(this))
  }

  _removeListeners() {
    if (this.variantType === 'color') {
      this?.colorSwatches?.forEach((swatch) =>
        swatch.removeEventListener('click', this._updateCardVariantState)
      )
    } else if (this.variantType === 'size') {
      this?.sizeButtons?.forEach((button) =>
        button.removeEventListener('click', this._updateCardVariantState)
      )
    }

    this.slideoutToggle = this.querySelector('.product-card__edit-variant')

    if (this.slideoutToggle) {
      this.slideoutToggle.removeEventListener(
        'click',
        this._updateSlideoutContent.bind(this)
      )
    }
    // this.productForm.addEventListener('submit', this.addToCart.bind(this))
  }

  _formatCurrency(money) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    })

    return formatter.format(money / 100)
  }

  _handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^\w\u00C0-\u024f]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  _handleSlideoutFilterKeydown(e) {
    let newButton
    if (e.code === 'ArrowLeft') {
      newButton = e.target
        .closest('li')
        ?.previousSibling?.querySelector('button')
    } else if (e.code === 'ArrowRight') {
      newButton = e.target.closest('li')?.nextSibling?.querySelector('button')
    }

    if (newButton) {
      this._handleSlideoutFilter(newButton, newButton.getAttribute('value'))
    }
  }

  _handleSlideoutFilter(target, category) {
    // Clear active state
    let filterButtons = [
      ...this.slideout.querySelectorAll('.shade-slider button'),
    ]

    filterButtons.forEach((button) => {
      button.classList.remove('active')
      button.setAttribute('tabindex', '-1')
    })

    // Set active state
    target.classList.add('active')
    target.setAttribute('tabindex', '0')
    target.focus()

    // Filter swatches
    let allSlideoutSwatches = [
      ...document.querySelectorAll(
        '.slideout-swatch-selector .swatches-container .swatch-element'
      ),
    ]

    allSlideoutSwatches.forEach((swatch) =>
      swatch.parentElement.classList.remove('hidden')
    )

    if (category !== 'all-shades') {
      allSlideoutSwatches.forEach((swatch) => {
        if (!swatch.classList.contains(this._handleize(category))) {
          swatch.parentElement.classList.add('hidden')
        }
      })
    } else {
      filterButtons[0].classList.add('active')
      allSlideoutSwatches.forEach((swatch) => {
        swatch.parentElement.classList.remove('hidden')
      })
    }

    // Select first swatch element after changing tabs
    const firstSwatchRadio = this.slideout.querySelector(
      '.js-slideout-swatches-container li:not(.hidden) .js-swatch-radio'
    )
    if (firstSwatchRadio) {
      firstSwatchRadio.checked = true
      firstSwatchRadio.dispatchEvent(new Event('change'))
    }
  }

  _createSwatchGroupSlider() {
    let categories = this.swatchGroupData
      .split(',')
      .filter((category) => !!category)
    let slider = this.slideout.querySelector(
      '.swatch-category-slider .shade-slider'
    )
    const simpleSlider = slider.closest('simple-slider')
    const swatchesTabpanel = this.slideout.querySelector(
      '.js-slideout-swatches-container'
    )

    slider.innerHTML = ''
    slider.scrollLeft = 0

    let tabPanelAriaLabelledBy = `swatch-tab-${this._handleize('All Shades')}`
    let allShadesButtonLi = this._createSwatchFilterButton('All Shades', '0')
    allShadesButtonLi.querySelector('button').classList.add('active')
    allShadesButtonLi.querySelector('button').addEventListener('click', (e) => {
      this._handleSlideoutFilter(e.target, 'all-shades')
    })
    allShadesButtonLi
      .querySelector('button')
      .addEventListener('keydown', (e) => {
        this._handleSlideoutFilterKeydown(e)
      })

    slider.appendChild(allShadesButtonLi)

    // for each loop over categories and create a slider li element
    categories.forEach((category) => {
      let liElement = this._createSwatchFilterButton(category, '-1')

      slider.appendChild(liElement)
      tabPanelAriaLabelledBy += ` swatch-tab-${this._handleize(category)}`
    })

    simpleSlider?.reInitSlider()

    swatchesTabpanel.setAttribute('aria-labelledby', tabPanelAriaLabelledBy)
  }

  _createSwatchFilterButton(category, tabindex = '0') {
    let buttonElement = document.createElement('button')
    buttonElement.classList.add('bold', 'uppercase', 'text-a2')
    buttonElement.setAttribute('tabindex', tabindex)
    buttonElement.value = this._handleize(category)
    buttonElement.innerText = category

    buttonElement.addEventListener('click', (e) => {
      this._handleSlideoutFilter(e.target, category)
    })
    buttonElement.addEventListener('keydown', (e) => {
      this._handleSlideoutFilterKeydown(e)
    })

    let liElement = document.createElement('li')
    liElement.setAttribute('data-slider-item', '')
    liElement.setAttribute('id', `swatch-tab-${this._handleize(category)}`)
    liElement.appendChild(buttonElement)

    return liElement
  }

  _handleNostoAttribution() {
    const nostoElement = this.closest('.nosto_element')

    if (!nostoElement) {
      // Clear any stale placement ID so a non-Nosto slideout ATC is not
      // falsely attributed to a previous Nosto interaction.
      if (this.slideout) delete this.slideout.dataset.nostoPlacementId
      return
    }

    this.triggerEvent('click::quick-shop-toggle', {
      product_id: this.productId,
      placement_id: nostoElement.id,
    })

    // Tag the slideout so a subsequent cart add from inside it can attribute
    // correctly — the slideout is not nested inside .nosto_element.
    if (this.slideout) {
      this.slideout.dataset.nostoPlacementId = nostoElement.id
    }
  }

  _updateSlideoutContent() {
    this._createSwatchGroupSlider()

    this.slideoutTitle.innerHTML = `<span class="text-glow text-glow--outline" data-text="${this.product.title.replace(
      /â¢/g,
      '™'
    )}">
    <span class="text-glow__inner">${this.product.title.replace(
      /â¢/g,
      '™'
    )}</span>
  </span>`
    this.slideoutSwatches.innerHTML = ''

    this.colorSwatches.forEach((swatch) => {
      let swatchClone = swatch.cloneNode(true)
      let swatchCloneButton = swatchClone.querySelector('.swatch-color-button')
      let bgColor = swatchCloneButton.style.backgroundColor

      let isButtonActive =
        swatchCloneButton.parentElement.parentElement.parentElement.classList.contains(
          'selected'
        )

      swatchCloneButton.style.background = ``
      swatchCloneButton.style.backgroundImage = `url('${this.swatchImageUrl}')`
      swatchCloneButton.style.backgroundSize = `cover`
      swatchCloneButton.style.backgroundPosition = `center`
      swatchCloneButton.style.backgroundColor = `${bgColor}`

      // Selected Variant Behavior
      if (isButtonActive) {
        // Update styles of current variant swatch
        // Update text/link of current variant
        this._swapSlideoutFeaturedVariant(swatchCloneButton)
      }

      swatchCloneButton.addEventListener('click', this._updateSlideoutVariant)
      const swatchCloneRadio = swatchCloneButton
        .closest('label')
        ?.querySelector('input[type="radio"]')
      swatchCloneRadio?.addEventListener(
        'change',
        this._handleSwatchRadioChange.bind(this)
      )
      swatchCloneRadio?.addEventListener(
        'keydown',
        this._handleSwatchRadioKeydown.bind(this)
      )

      let liElement = document.createElement('li')
      liElement.appendChild(swatchClone)

      let spanElement = document.createElement('span')
      spanElement.innerText = swatchCloneButton.dataset.value
      spanElement.classList.add('text-b2')
      liElement.appendChild(spanElement)

      this.slideoutSwatches.appendChild(liElement)
    })

    let offset =
      this.slideoutTitle.offsetHeight +
      this.slideoutHeader.offsetHeight +
      this.slideoutCategories.offsetHeight +
      5
    if (!this.mql.matches) {
      offset += this.slideoutPanel.offsetTop
    }
    this.slideoutSwatchContainer.style.height = `calc(100dvh - ${offset}px)`
  }

  _swapSlideoutFeaturedVariant(swatch) {
    // Set active state
    let allSlideoutSwatches = [
      ...document.querySelectorAll(
        '.slideout-swatch-selector .swatches-container .swatch-color-button'
      ),
    ]

    let swatchButton = swatch

    allSlideoutSwatches.forEach((swatchButton) =>
      swatchButton.classList.remove('selected')
    )

    swatchButton.classList.add('selected')

    // Get new variant info
    let selectedVariant = this.product.variants.find(
      (variant) => variant.id === parseFloat(swatchButton.dataset.variantId)
    )

    let slideoutVariantInput = document.querySelector(
      '.slideout-swatch-selector .js-selected-variant'
    )

    let slideoutPrice = document.querySelector(
      '.slideout-swatch-selector .atc-price'
    )

    let slideoutFeaturedVariantImage = document.querySelector(
      '.slideout-swatch-selector .atc-featured-variant__image span'
    )

    let slideoutFeaturedVariantTitle = document.querySelector(
      '.slideout-swatch-selector .atc-featured-variant__text .variant-title'
    )
    let slideoutFeaturedVariantDescription = document.querySelector(
      '.slideout-swatch-selector .atc-featured-variant__text .variant-description'
    )
    let slideoutFeaturedVariantLink = document.querySelector(
      '.slideout-swatch-selector .atc-featured-variant__text .variant-link'
    )
    let slideoutFeaturedVariantUrl = document.querySelector(
      '.slideout-swatch-selector .atc-featured-variant__link a'
    )

    slideoutVariantInput.value = selectedVariant.id
    slideoutVariantInput.setAttribute('data-product-id', this.productId)

    slideoutPrice.innerText = `${this._formatCurrency(selectedVariant.price)}`

    slideoutFeaturedVariantImage.style.backgroundColor =
      swatchButton.style.backgroundColor
    slideoutFeaturedVariantImage.style.backgroundImage =
      swatchButton.style.backgroundImage
    slideoutFeaturedVariantImage.style.backgroundPosition =
      swatchButton.style.backgroundPosition
    slideoutFeaturedVariantImage.style.backgroundSize =
      swatchButton.style.backgroundSize

    slideoutFeaturedVariantTitle.innerHTML = swatchButton.dataset.value
    slideoutFeaturedVariantDescription.innerHTML =
      swatchButton?.dataset?.description

    if (swatchButton?.dataset?.description !== '') {
      slideoutFeaturedVariantLink.classList.remove('hidden')
    } else {
      slideoutFeaturedVariantLink.classList.add('hidden')
    }

    slideoutFeaturedVariantUrl.href =
      '/products/' + this.product.handle + '?variant=' + selectedVariant.id

    // Update Mini Product Card Variant
    this.querySelector('.product-card__current-variant-title').innerText =
      swatch.dataset.value

    this.querySelector('.swatch-color-span').style.backgroundColor =
      swatch.style.backgroundColor

    let comparePrice = this.querySelector(
      '.price .price__compare .price-item--regular'
    )
    let normalPrice = this.querySelector('.price  .price-item--sale')
    if (selectedVariant?.compare_at_price) {
      comparePrice.classList.remove('hidden')

      comparePrice.innerText = `${this._formatCurrency(
        selectedVariant.compare_at_price
      )}`

      normalPrice.innerText = `${this._formatCurrency(selectedVariant.price)}`
    } else {
      comparePrice.classList.add('hidden')
      normalPrice.innerText = `${this._formatCurrency(selectedVariant.price)}`
    }

    // Update Add to Cart Button on mini card

    let slideoutAtcButton = document.querySelector(
      '.slideout-swatch__atc button'
    )
    const preRelease = selectedVariant.pre_release === true
    this._updateCardAtcButton(selectedVariant, preRelease)
    this._setPreReleaseButton(slideoutAtcButton, selectedVariant, preRelease)

    if (preRelease) {
      slideoutAtcButton.classList.remove('disabled')
      slideoutAtcButton.removeAttribute('disabled')
      slideoutAtcButton.querySelector('.atc-separator').classList.add('hidden')
      slideoutAtcButton.querySelector('span').innerText =
        atcStatusTitle.joinWaitlist
      slideoutAtcButton.querySelector('.atc-price').innerText = ''
    } else if (!selectedVariant.available) {
      slideoutAtcButton.classList.add('disabled')
      slideoutAtcButton.setAttribute('disabled', true)
      slideoutAtcButton.querySelector('.atc-separator').classList.add('hidden')
      slideoutAtcButton.querySelector('span').innerText =
        slideoutAtcButton.dataset.oosText
      slideoutAtcButton.querySelector('.atc-price').innerText = ''
    } else {
      slideoutAtcButton.classList.remove('disabled')
      slideoutAtcButton.removeAttribute('disabled')
      slideoutAtcButton
        .querySelector('.atc-separator')
        .classList.remove('hidden')
      slideoutAtcButton.querySelector('span').innerText =
        slideoutAtcButton.dataset.inStockText

      slideoutAtcButton.querySelector(
        '.atc-price'
      ).innerText = `${this._formatCurrency(selectedVariant.price)}`
    }

    // Update Add to Cart Button on slideout
  }

  _updateCardAtcButton(variant, preRelease) {
    const unavailable = !preRelease && !variant.available
    this.atcButton.classList.toggle('disabled', unavailable)
    this.atcButton.toggleAttribute('disabled', unavailable)
    this.atcButton.querySelector('span').innerText = preRelease
      ? atcStatusTitle.joinWaitlist
      : unavailable
        ? this.atcButton.dataset.oosText
        : this.atcButton.dataset.inStockText
    this._setPreReleaseButton(this.atcButton, variant, preRelease)
  }

  _setPreReleaseButton(button, variant, preRelease) {
    button.toggleAttribute('data-pre-release', preRelease)
    button.classList.toggle('js-open-slideout', preRelease)
    button.classList.toggle(atcStatusTitle.notifyMeClass[0], preRelease)
    if (!preRelease) {
      button.removeAttribute('data-target')
      return
    }
    const notifyMeData = {
      'data-target': atcStatusTitle.targetID,
      'data-variant-id': variant.id,
      'data-product-id': this.product.id,
      'data-sku': variant.sku || '',
      'data-product-title': this.product.title || '',
      'data-variant-title': variant.title || '',
      'data-image':
        variant.featured_image?.src || this.product.featured_image || '',
    }
    Object.entries(notifyMeData).forEach(([k, v]) => button.setAttribute(k, v))
  }

  _handleSwatchRadioChange(e) {
    const swatchButton = e.target
      .closest('label')
      .querySelector('.swatch-color-button')
    if (swatchButton) {
      this._swapSlideoutFeaturedVariant(swatchButton)
    }
  }

  _handleSwatchRadioKeydown(e) {
    if (e.code === 'Enter') {
      this.slideout
        .querySelector('#slideout-swatch-selector-form')
        ?.dispatchEvent(new Event('submit'))
    }
  }

  _updateSlideoutVariant(e) {
    this._swapSlideoutFeaturedVariant(e.target)
  }

  _updateCardVariantState(e) {
    // Update Active Swatch
    this._clearActiveButton()
    let clickedSwatch = e.target
    let clickedSwatchParent =
      clickedSwatch.parentElement.parentElement.parentElement

    clickedSwatchParent.classList.add('selected')

    if (this.sizeButtons.length > 0) {
      clickedSwatchParent = clickedSwatch.parentElement.parentElement
    }

    clickedSwatchParent.classList.add('selected')

    // Update Pricing + Availability
    let selectedVariant = this.product.variants.find(
      (variant) => variant.id === parseFloat(clickedSwatch.dataset.variantId)
    )

    if (this.selectedVariantInput)
      this.selectedVariantInput.value = selectedVariant.id

    this._updateCardAtcButton(
      selectedVariant,
      selectedVariant.pre_release === true
    )

    let comparePrice = this.querySelector(
      '.price .price__compare .price-item--regular'
    )
    let normalPrice = this.querySelector('.price  .price-item--sale')
    if (selectedVariant?.compare_at_price) {
      comparePrice.classList.remove('hidden')

      comparePrice.innerText = `${this._formatCurrency(
        selectedVariant.compare_at_price
      )}`

      normalPrice.innerText = `${this._formatCurrency(selectedVariant.price)}`
    } else {
      comparePrice.classList.add('hidden')
      normalPrice.innerText = `${this._formatCurrency(selectedVariant.price)}`
    }

    // Update Variant text or hide text

    let variantText = this.querySelector('.product-card__current-variant-title')
    variantText.innerText = selectedVariant.title
  }

  _clearActiveButton() {
    if (this.sizeButtons.length === 0) {
      this.colorSwatches.forEach((swatch) => {
        swatch
          .querySelector('.color-select-container')
          .classList.remove('selected')
      })
    } else {
      this.sizeButtons.forEach((button) => {
        button.parentElement.parentElement.classList.remove('selected')
      })
    }
  }

  static styles = [
    visuallyHidden,
    css`
      .hidden {
        display: none !important;
      }
    `,
  ]
}

window.customElements.define('mini-product-card', MiniProductCard)
